import React, { useState, useRef } from 'react';
import { UploadCloud, Music, CheckCircle2, Loader2, X, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { track } from '@/lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_MB = 30;
const MAX_ART_MB = 8;
const AUDIO_ACCEPT = '.mp3,audio/mpeg';
const ART_ACCEPT = '.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp';

export default function SubmitMusicPage() {
  const fileInputRef = useRef(null);
  const artworkInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [artwork, setArtwork] = useState(null);
  const [artworkPreview, setArtworkPreview] = useState('');
  const [form, setForm] = useState({
    artist_name: '', email: '', song_title: '', notes: '',
    is_album: false, album_name: '',
    rights: false,
  });
  const [progress, setProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submittedRef, setSubmittedRef] = useState(null);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const pickFile = (f) => {
    if (!f) return;
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (ext !== 'mp3') {
      toast.error('Only MP3 files are accepted');
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`File too large. Max ${MAX_MB}MB`);
      return;
    }
    setFile(f);
  };

  const pickArtwork = (f) => {
    if (!f) return;
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (!['jpg', 'jpeg', 'png', 'webp'].includes(ext)) {
      toast.error('Artwork must be a JPG, PNG or WebP image');
      return;
    }
    if (f.size > MAX_ART_MB * 1024 * 1024) {
      toast.error(`Artwork too large. Max ${MAX_ART_MB}MB`);
      return;
    }
    if (artworkPreview) URL.revokeObjectURL(artworkPreview);
    setArtwork(f);
    setArtworkPreview(URL.createObjectURL(f));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.[0]) pickFile(e.dataTransfer.files[0]);
  };

  const clearArtwork = () => {
    if (artworkPreview) URL.revokeObjectURL(artworkPreview);
    setArtwork(null);
    setArtworkPreview('');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error('Please attach an MP3 audio file');
    if (!form.rights) return toast.error('Please confirm you own the rights');
    if (!form.artist_name.trim() || !form.email.trim() || !form.song_title.trim()) {
      return toast.error('Please fill in artist, email, and song title');
    }
    if (form.is_album && !form.album_name.trim()) {
      return toast.error('Please provide the album name');
    }

    const data = new FormData();
    data.append('file', file);
    data.append('artist_name', form.artist_name.trim());
    data.append('email', form.email.trim());
    data.append('song_title', form.song_title.trim());
    data.append('notes', form.notes.trim());
    data.append('rights_confirmed', 'true');
    data.append('is_album', form.is_album ? 'true' : 'false');
    data.append('album_name', form.is_album ? form.album_name.trim() : '');
    if (artwork) data.append('artwork', artwork);

    setSubmitting(true);
    setProgress(0);
    try {
      const res = await axios.post(`${API}/music-submissions`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      setSubmittedRef(res.data);
      track('music_submit', {
        size_mb: Math.round((file.size / (1024*1024)) * 10) / 10,
        has_artwork: !!artwork,
        is_album: form.is_album,
      });
      toast.success('Submission received. A confirmation email is on its way.');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Submission failed. Please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submittedRef) {
    return (
      <div data-testid="submit-music-success" className="min-h-screen bg-[#0F1115] text-white pb-32">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-[#171A21] border border-white/5 rounded-2xl p-10 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-6">
              <CheckCircle2 size={32} className="text-[#D4AF37]" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-3" style={{ fontFamily: 'Clash Display, sans-serif' }}>
              Submission received
            </h1>
            <p className="text-[#94A3B8] mb-2">
              We received <span className="text-white font-medium">"{submittedRef.song_title}"</span> by{' '}
              <span className="text-white font-medium">{submittedRef.artist_name}</span>.
            </p>
            <p className="text-[#94A3B8] text-sm mb-8">
              A confirmation email has been sent to your inbox. Our team will review your track and reach out if it's selected for broadcast.
            </p>
            <button
              data-testid="submit-another-btn"
              onClick={() => {
                clearArtwork();
                setSubmittedRef(null);
                setFile(null);
                setForm({ artist_name: '', email: '', song_title: '', notes: '', is_album: false, album_name: '', rights: false });
                setProgress(0);
              }}
              className="bg-[#D4AF37] text-[#0F1115] font-semibold hover:bg-[#E8C24D] rounded-full px-6 py-2.5 text-sm"
            >
              Submit another track
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="submit-music-page" className="min-h-screen bg-[#0F1115] text-white pb-32">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 mb-4">
            <Music size={14} className="text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs font-medium tracking-widest uppercase">For Artists</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-3" style={{ fontFamily: 'Clash Display, sans-serif' }}>
            Submit your music
          </h1>
          <p className="text-[#94A3B8] text-sm sm:text-base max-w-lg">
            Are you an artist or rights owner making contemporary African Christian music? Send us your track for consideration.
          </p>
          <p className="text-[#64748B] text-xs mt-3">
            Already had a track submitted or aired and want it removed?{' '}
            <a href="/takedown" data-testid="submit-to-takedown-link" className="text-[#94A3B8] hover:text-[#D4AF37] underline">
              Request a takedown
            </a>.
          </p>
        </div>

        <form onSubmit={submit} className="bg-[#171A21] border border-white/5 rounded-2xl p-6 sm:p-8 space-y-6">
          {/* Audio dropzone */}
          <div>
            <label className="text-xs uppercase tracking-widest text-[#94A3B8] font-medium">Audio file (MP3) *</label>
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              data-testid="audio-dropzone"
              className="mt-2 border border-dashed border-white/10 hover:border-[#D4AF37]/40 hover:bg-white/[0.02] rounded-xl p-8 text-center cursor-pointer transition-colors"
            >
              <input
                ref={fileInputRef}
                data-testid="audio-file-input"
                type="file"
                accept={AUDIO_ACCEPT}
                onChange={(e) => pickFile(e.target.files?.[0])}
                className="hidden"
              />
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <Music size={20} className="text-[#D4AF37]" />
                  <div className="text-left">
                    <div className="text-sm text-white font-medium truncate max-w-[280px]">{file.name}</div>
                    <div className="text-xs text-[#94A3B8]">{(file.size / (1024*1024)).toFixed(2)} MB</div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setFile(null); }}
                    className="ml-2 p-1.5 rounded-full hover:bg-white/10 text-[#94A3B8] hover:text-white"
                    aria-label="Remove file"
                    data-testid="remove-audio-btn"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="text-[#94A3B8]">
                  <UploadCloud size={28} className="mx-auto text-[#D4AF37] mb-3" />
                  <p className="text-sm text-white font-medium">Drop your MP3 here or click to browse</p>
                  <p className="text-xs mt-1">MP3 only — up to {MAX_MB}MB</p>
                </div>
              )}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#94A3B8] font-medium">Artist / Band *</label>
              <input
                data-testid="artist-name-input"
                type="text"
                value={form.artist_name}
                onChange={(e) => set('artist_name', e.target.value)}
                required
                className="mt-2 w-full bg-[#0F1115] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#94A3B8] font-medium">Contact email *</label>
              <input
                data-testid="email-input"
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                required
                className="mt-2 w-full bg-[#0F1115] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40"
              />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-[#94A3B8] font-medium">Song title *</label>
            <input
              data-testid="song-title-input"
              type="text"
              value={form.song_title}
              onChange={(e) => set('song_title', e.target.value)}
              required
              className="mt-2 w-full bg-[#0F1115] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40"
            />
          </div>

          {/* Album */}
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                data-testid="is-album-checkbox"
                type="checkbox"
                checked={form.is_album}
                onChange={(e) => set('is_album', e.target.checked)}
                className="mt-1 w-4 h-4 accent-[#D4AF37] cursor-pointer"
              />
              <span className="text-[#94A3B8] text-sm group-hover:text-white transition-colors">
                This track is part of an album
              </span>
            </label>
            {form.is_album && (
              <div>
                <label className="text-xs uppercase tracking-widest text-[#94A3B8] font-medium">Album name *</label>
                <input
                  data-testid="album-name-input"
                  type="text"
                  value={form.album_name}
                  onChange={(e) => set('album_name', e.target.value)}
                  required
                  className="mt-2 w-full bg-[#0F1115] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40"
                />
              </div>
            )}
          </div>

          {/* Artwork */}
          <div>
            <label className="text-xs uppercase tracking-widest text-[#94A3B8] font-medium">
              Artwork <span className="text-[#64748B] normal-case tracking-normal">(optional — JPG, PNG or WebP, up to {MAX_ART_MB}MB)</span>
            </label>
            <div
              onClick={() => artworkInputRef.current?.click()}
              data-testid="artwork-dropzone"
              className="mt-2 flex items-center gap-4 border border-dashed border-white/10 hover:border-[#D4AF37]/40 hover:bg-white/[0.02] rounded-xl p-4 cursor-pointer transition-colors"
            >
              <input
                ref={artworkInputRef}
                data-testid="artwork-input"
                type="file"
                accept={ART_ACCEPT}
                onChange={(e) => pickArtwork(e.target.files?.[0])}
                className="hidden"
              />
              {artworkPreview ? (
                <img src={artworkPreview} alt="Artwork preview" data-testid="artwork-preview" className="w-20 h-20 rounded-lg object-cover shrink-0" />
              ) : (
                <div className="w-20 h-20 rounded-lg bg-white/[0.03] border border-white/5 flex items-center justify-center shrink-0">
                  <ImageIcon size={24} className="text-[#94A3B8]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                {artwork ? (
                  <>
                    <p className="text-sm text-white font-medium truncate">{artwork.name}</p>
                    <p className="text-xs text-[#94A3B8]">{(artwork.size / (1024*1024)).toFixed(2)} MB · tap to change</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-white font-medium">Add cover art</p>
                    <p className="text-xs text-[#94A3B8]">Square works best (1400×1400)</p>
                  </>
                )}
              </div>
              {artwork && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); clearArtwork(); }}
                  className="p-1.5 rounded-full hover:bg-white/10 text-[#94A3B8] hover:text-white shrink-0"
                  aria-label="Remove artwork"
                  data-testid="remove-artwork-btn"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-[#94A3B8] font-medium">Notes (optional)</label>
            <textarea
              data-testid="notes-input"
              rows={3}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="Anything you'd like the DJ to know — release date, streaming links, social handles, etc."
              className="mt-2 w-full bg-[#0F1115] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40 resize-none"
            />
          </div>

          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              data-testid="rights-checkbox"
              type="checkbox"
              checked={form.rights}
              onChange={(e) => set('rights', e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#D4AF37] cursor-pointer"
              required
            />
            <span className="text-[#94A3B8] text-sm group-hover:text-white transition-colors">
              I confirm that I am the rights owner (or authorised representative) and I grant Christovibes Radio a non-exclusive licence to broadcast this recording. *
            </span>
          </label>

          {submitting && (
            <div>
              <div className="flex justify-between text-xs text-[#94A3B8] mb-1">
                <span>Uploading…</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-[#D4AF37] transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}

          <button
            data-testid="submit-music-btn"
            type="submit"
            disabled={submitting}
            className="w-full bg-[#D4AF37] text-[#0F1115] font-semibold hover:bg-[#E8C24D] rounded-full px-6 py-3 flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
          >
            {submitting ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : 'Submit for review'}
          </button>
        </form>
      </div>
    </div>
  );
}
